import { useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import {
  httpService,
  IconWrapper,
  REGEX,
  PasswordValidator,
} from '@flexo/general';
import { Button, Textfield } from '@flexo/atoms';
import { Player } from '@lottiefiles/react-lottie-player';
import { v4 as uuidv4 } from 'uuid';

import styles from './password-reset.module.scss';
import { ALERT  } from '@flexo/reducers';
import { useDispatch } from 'react-redux';

const ENV = (import.meta as any).env;
const loader = '/loaderLottie.json';
let _askError = false;
let ranOnce = false;

let _formdata = {
  password: '',
  password_confirm: '',
};

export function PasswordResetWeb() {
  const formRef = useRef<any>(null);
  const [searchParams] = useSearchParams();

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [passwordresetAsked, setPasswordResetAsked] = useState(false);
  const [askError, setAskError] = useState(false);
  const [paramsData, setParamsData] = useState({
    email: '',
    recovery_code: '',
  });

  const { title, subtitle, inputs, buttonLabel } = t(
    'pages.password_reset_web',
    { returnObjects: true, email: paramsData.email }
  ) as any;
  const [samePassword, setSamePassword] = useState(true);
  const [formData, setFormData] = useState(_formdata);

  const [checkList, setChecklist] = useState<any>(
    Object.values(inputs.checkList).reduce(
      (prev: any, next: any) => ({ ...prev, [next.name]: false }),
      {}
    )
  );

  const formRules = Object.values(inputs.checkList).map((checkItem: any) => ({
    name: checkItem.name,
    rule: checkItem.rule,
  }));

  const [toggleIcons, setToggleIcons] = useState({
    password: 'hide',
    password_confirm: 'hide',
  });

  const [toggleTypes, setToggleTypes] = useState({
    password: 'password',
    password_confirm: 'password',
  });

  const debounce = (fn, delay = 100) => {
    const handler = setTimeout(() => {
      fn();
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  };

  async function handleInputChange(event: any) {
    const { name, value } = event.target;

    if (name === 'password_confirm') {
      setSamePassword(true);
    }

    _askError = false;
    setAskError(_askError);

    _formdata = { ..._formdata, [name]: value };
    setFormData(_formdata);

    if (name === 'password') {
      checkValidation(value);
    }
  }

  function togglePasswordVisibility(inputName: string) {
    setToggleIcons({
      ...toggleIcons,
      [inputName]: toggleIcons[inputName] === 'hide' ? 'show' : 'hide',
    });

    setToggleTypes({
      ...toggleTypes,
      [inputName]: toggleTypes[inputName] === 'password' ? 'text' : 'password',
    });
  }

  function checkValidation(value: string) {
    // Use centralized password validation
    const validationResults = PasswordValidator.validatePassword(value);
    setChecklist(validationResults);
  }

  async function askForPasswordreset() {
    setPasswordResetAsked(true);
    
    const response = await httpService
      .post({
        url: `${ENV.VITE_APP_BASE_URL}/auth/v1_1/password-change-confirm`,
        data: {
          email: paramsData.email,
          reset_code: paramsData.recovery_code,
          new_password: formData.password,
        },
      })
      .then((resp: any) => ({
        status: resp.status,
        statusText: resp?.statusText || 'Unknown status text',
        message: resp?.data?.message || 'Unknown message',
      }))
      .catch((error) => {
        // console.warn(error);

        return {
          status: error?.response?.status || 500,
          statusText: error?.response?.statusText || 'Unknown error',
        };
      });

    setPasswordResetAsked(false);

    if (response.status > 200) {
      if (response.status === 422) {
        dispatch(
          ALERT.SetAlert({
            payload: {
              value: {
                type: 'BANNER_WRONG_REQUEST_DATA',
              },
            },
          } as any)
        );
      } else if (response.status === 403) {
        dispatch(
          ALERT.SetAlert({
            payload: {
              value: {
                type: 'BANNER_NOT_ALLOWED',
              },
            },
          } as any)
        );
      } else if (response.status === 400) {
        dispatch(
          ALERT.SetAlert({
            payload: {
              value: {
                type: 'BANNER_INVALID_CREDENTIALS',
              },
            },
          } as any)
        );
      } else {
        dispatch(
          ALERT.SetAlert({
            payload: {
              value: {
                type: 'BANNER_UNEXPECTED_ERROR',
              },
            },
          } as any)
        );
      }

      _askError = true;
      setAskError(_askError);
    } else {
      _askError = false;
      setAskError(_askError);

      // Set password updated flags in storage (same pattern as update-credentials-form)
      localStorage.setItem('passwordUpdated', 'true');
      sessionStorage.setItem('passwordUpdated', 'true');
      navigate('/login');
    }
  }

  function checkConfirmationPassword(e: any) {
    if (e.key === 'Backspace') {
      setSamePassword(true);
    } else {
      debounce(() => {
        if (formData.password !== formData.password_confirm) {
          setSamePassword(false);
        } else {
          setSamePassword(true);
        }
      }, 500);
    }
  }

  function focusChild(event: any) {
    try {
      const parent = event.target;
      const child = parent.querySelector('input');
      child.focus();
    } catch (error) {
      console.warn(error);
    }
  }

  useEffect(() => {
    if (ranOnce) return;

    const email: string | null = searchParams.get('email') || null;
    const recoveryCode: string | null = searchParams.get('recovery_code') || null;

    // eslint-disable-next-line
    if ( email && recoveryCode ) {
      setParamsData({
        email: email,
        recovery_code: recoveryCode,
      });
    } else {
      dispatch(
        ALERT.SetAlert({
          payload: {
            value: {
              type: 'BANNER_WRONG_PARAMS',
            },
          },
        } as any)
      );

      setTimeout(() => {
        dispatch(ALERT.ResetAlert({} as any));

        navigate('/login');
      }, 4000);
    }

    ranOnce = true;
  }, []);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();

        // Controlla se i dati sono validi prima di inviare il form
        const isPasswordValid = REGEX.password.test(formData.password);
        const isConfirmationValid =
          formData.password === formData.password_confirm;

        if (!isPasswordValid || !isConfirmationValid || passwordresetAsked)
          return;

        askForPasswordreset();
      }
    }

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [formData, passwordresetAsked]);

  return (
    <div className={styles.PasswordResetWeb}>
      <div className={styles.PasswordResetWeb__Header}>
        <IconWrapper
          iconName={'logo_wide'}
          className={styles.PasswordResetWeb__Header__Logo}
        />
        <div className={styles.PasswordResetWeb__Header__Title}>{title}</div>
        <div className={styles.PasswordResetWeb__Header__Email}>{subtitle}</div>
      </div>

      <form
        id="password_reset_web_form"
        className={styles.PasswordResetWeb__Form}
        ref={formRef}
      >
        {/* password */}

        <div
          className={styles.PasswordResetWeb__Form__Input}
          onClick={(e) => focusChild(e)}
        >
          <Textfield
            variant="default"
            label={inputs.password.label}
            name={inputs.password.name}
            type={toggleTypes[inputs.password.name]}
            placeholder={inputs.password.placeholder}
            onPaste={(e) => false}
            onCopy={(e) => false}
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck="false"
            autoComplete="off"
            defaultValue={formData[inputs.password.name]}
            onChange={(ev: any) => handleInputChange(ev)}
            icon={toggleIcons[inputs.password.name]}
            onIconClick={() => togglePasswordVisibility(inputs.password.name)}
            underline={askError === true ? 'visible' : 'hidden'}
          />
        </div>

        {/* generalities */}

        <div className={styles.PasswordResetWeb__Form__Input}>
          {inputs.checkList.map((checkItem, _index: number) => (
            <div
              key={`${checkItem.name}-${_index}`}
              className={styles.PasswordResetWeb__Form__Checklist__Item}
            >
              <span
                className={
                  styles.PasswordResetWeb__Form__Checklist__Item__Checkbox
                }
              >
                {checkList[checkItem.name] === true ? (
                  <IconWrapper iconName={'check-encircled_outline'} />
                ) : (
                  <IconWrapper iconName={'ellipse'} />
                )}
              </span>
              <p
                className={styles.PasswordResetWeb__Form__Checklist__Item__Text}
              >
                {checkItem.label}
              </p>
            </div>
          ))}
        </div>

        {/* password_confirm */}

        <div
          className={styles.PasswordResetWeb__Form__Input}
          onClick={(e) => focusChild(e)}
        >
          <Textfield
            variant="default"
            label={inputs.password_confirm.label}
            name={inputs.password_confirm.name}
            type={toggleTypes[inputs.password_confirm.name]}
            placeholder={inputs.password_confirm.placeholder}
            onPaste={(e) => false}
            onCopy={(e) => false}
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck="false"
            defaultValue={formData[inputs.password_confirm.name]}
            onChange={handleInputChange}
            autoComplete="off"
            icon={toggleIcons[inputs.password_confirm.name]}
            onIconClick={() =>
              togglePasswordVisibility(inputs.password_confirm.name)
            }
            underline={
              askError === true || samePassword === false ? 'visible' : 'hidden'
            }
            onBlur={checkConfirmationPassword}
        
          />
        </div>
      </form>

      <div className={styles.PasswordResetWeb__Footer}>
        <Button
          className={styles.PasswordResetWeb__Footer__Button}
          label={
            passwordresetAsked ? (
              <Player
                autoplay
                loop
                src={loader}
                style={{ height: 20, transform: 'scale(0.7)' }}
              />
            ) : (
              buttonLabel
            )
          }
          variant="primary"
          color="secondary"
          disabled={
            !REGEX.password.test(formData.password) ||
            formData.password !== formData.password_confirm
          }
          onClick={() => askForPasswordreset()}
        />
      </div>
    </div>
  );
}
